import { ol_Feature, ol_source } from "@omnia/exports/openlayers"
import { FieldViewModel, groupBy } from "../omnia"

import IMapTopologyService = omnia.map.IMapTopologyService

export class FieldVm {

    public static create(fields: FieldViewModel[], years: number[], topo: IMapTopologyService) {


        let activeBoundaries: ol_source.Vector = null

        const vm = fields.map(f => {

            return new FieldVm(f, years.map(y => new FieldYearVm(f, y)))

        })

        activeBoundaries = createActiveBoundariesSource(
            vm.filter(f => f.field.hasBoundary())
        )

        vm.forEach(f => f.overlapping = getOverlapping(f.id, activeBoundaries, topo))

        return vm

    }

    constructor(
        public readonly field: FieldViewModel,
        public readonly years: FieldYearVm[],
        public readonly id = field.getId(),
        public overlapping = [] as FieldVm[],
        public ignoreBoundary = field.field.IgnoreBoundary) {

        years.forEach(y => {
            y.prevYear = years.find(p => p.year == y.year - 1)
            y.nextYear = years.find(n => n.year == y.year + 1)
        })
    }

    get name() {
        return this.field.fieldName
    }

    get description() {
        return this.field.description
    }

    hasBoundary() {
        return this.field.hasBoundary()
    }

    hasBoundaryStatusChanged() {
        return this.ignoreBoundary != this.field.field.IgnoreBoundary
    }

    hasChanged() {
        return this.hasBoundaryStatusChanged() || this.years.some(y => y.hasChanged())
    }

    hasEmptyYears() {
        return !(this.years.every(y => !y.disabled))
    }

    canIgnoreBoundary() {
        return this.hasBoundary() && this.years.some(y => y.disabled)
    }

    getOverlapping() {
        return this.overlapping.filter(o => !(o.years.some(y => y.disabled) && o.ignoreBoundary))
    }

    canEnableBoundary() {
        const overlapping = this.getOverlapping()
        return this.hasBoundary() && this.ignoreBoundary && (!overlapping.length)
    }

    toggleBoundary() {
        if (this.canEnableBoundary) {
            this.ignoreBoundary = !this.ignoreBoundary
        }
    }

    getBoundaryTooltip() {

        const msgEnable = "Enable the field boundary"
        const msgIgnore = "Ignore the field boundary"

        if (this.ignoreBoundary && !this.canEnableBoundary()) {
            const overlapping = this.getOverlapping()
            const fieldNames = overlapping.map(o => o.name).join(", ")
            const msg = "The boundary cannot be enabled back because it overlaps the following active fields: "
            return msg + fieldNames
        }

        return this.ignoreBoundary
            ? msgEnable
            : msgIgnore
    }

}

export type FieldYearCropVm = {
    name: string;
    cssColor: string;
};

export class FieldYearVm {

    public readonly crops: FieldYearCropVm[];

    prevYear: FieldYearVm
    nextYear: FieldYearVm

    readonly fieldId: number

    disabled: boolean
    private readonly _initDisabled: boolean

    constructor(field: FieldViewModel, public readonly year: number) {

        this.fieldId = field.fieldId
        this.disabled = this._initDisabled = field.isDisabledOnYear(year)

        this.crops = this.disabled
            ? null
            : getFieldCrops(field, year)

    }

    hasChanged() {
        return this.disabled != this._initDisabled
    }

    canDisable() {
        return (!this.crops) && (!this.disabled)
    }

    toggleDisabled(target?: boolean) {
        target ??= !this.disabled;
        if (!target || this.canDisable()) {
            this.disabled = target;
        }
    }

}

function createActiveBoundariesSource(activeBoundaries: FieldVm[]) {

    const features = omnia.map.createFeatures(activeBoundaries, f => f.field.getBoundary(), f => f.id, f => f)

    return new ol_source.Vector({
        features,
        useSpatialIndex: true
    })

}

function getOverlapping(fieldId: string, source: ol_source.Vector, topo: IMapTopologyService) {

    const fieldFeature = source.getFeatureById(fieldId)

    if (!fieldFeature) {
        return []
    }

    if (!(fieldFeature instanceof ol_Feature)) {
        return []
    }

    const fieldGeometry = fieldFeature.getGeometry()

    const potentialOverlaps = source
        .getFeaturesInExtent(fieldFeature.getGeometry().getExtent())
        .map(f => ({
            geom: f.getGeometry(),
            field: f.get("data") as FieldVm
        }))
        .filter(f => f.field.id != fieldId)

    const overlaps = potentialOverlaps.filter(f => !topo.disjoint(fieldGeometry, f.geom))

    return overlaps.map(o => o.field)

}

function getFieldCrops(field: FieldViewModel, year: number) {

    const cropping = field.croppings
        .filter(c => c.yearId == year)
        .map(c => ({
            uncropped: c.uncropped,
            name: c.getCropName(),
            color: c.cssColor
        }))

    const groups = groupBy(cropping, c => c.name)

    const crops = groups
        .filter(g => g.values.some(v => !v.uncropped) || (g.values.length > 1))
        .map(g => ({
            name: g.key,
            cssColor: g.values[0].color
        }))

    if (!crops.length) {
        return null
    }

    return crops

}

